import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import plaidService, { Transaction } from '../services/plaidService';
import Button from '../components/Button';
import HomeButton from '../components/HomeButton';
import BudgetSection from '../components/BudgetSection';
import styles from './TransactionsPage.module.css';
import { usePlaidLink } from 'react-plaid-link';

const TransactionsPage: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const initializePlaid = async () => {
      try {
        setLoading(true);
        const token = await plaidService.createLinkToken();
        setLinkToken(token);
        const txs = await plaidService.getPersistedTransactions();
        setTransactions(txs);
      } catch (err) {
        setError('Failed to initialize Plaid');
        console.error('Error initializing Plaid:', err);
      } finally {
        setLoading(false);
      }
    };

    initializePlaid();
  }, [navigate]);

  const handleRefresh = async () => {
    try {
      setLoading(true);
      await plaidService.fetchTransactions();
      const txs = await plaidService.getPersistedTransactions();
      setTransactions(txs);
    } catch (err) {
      setError('Failed to refresh transactions');
      console.error('Error refreshing transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  const onSuccess = async (publicToken: string) => {
    try {
      setLoading(true);
      setError(null);
      console.log('Exchanging public token:', publicToken);
      await plaidService.exchangePublicToken(publicToken);
      await plaidService.fetchTransactions();
      const txs = await plaidService.getPersistedTransactions();
      setTransactions(txs);
    } catch (err) {
      setError('Failed to connect bank account');
      console.error('Error connecting bank account:', err);
    } finally {
      setLoading(false);
    }
  };

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess,
    onExit: () => {
      console.log('Plaid Link exited');
    },
    onEvent: (eventName, metadata) => {
      console.log('Plaid Link event:', eventName, metadata);
    },
  });

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Budget calculation logic (copied from BudgetBreakdown)
  type Frequency = 'daily' | 'weekly' | 'biweekly' | 'monthly';
  const [monthlyBudget, setMonthlyBudget] = useState<number>(0);

  useEffect(() => {
    const savedIncome = localStorage.getItem('budget_income');
    const savedFrequency = localStorage.getItem('budget_frequency') as Frequency | null;
    if (savedIncome) {
      const incomeNum = parseFloat(savedIncome);
      if (!isNaN(incomeNum) && incomeNum > 0) {
        let monthly = 0;
        switch (savedFrequency) {
          case 'daily':
            monthly = incomeNum * 30;
            break;
          case 'weekly':
            monthly = incomeNum * 4.33;
            break;
          case 'biweekly':
            monthly = incomeNum * 2.17;
            break;
          default:
            monthly = incomeNum;
        }
        setMonthlyBudget(monthly);
      }
    }
  }, []);

  return (
    <div className={styles.container}>
      <HomeButton />
      <h1>Transactions</h1>

      <div className={styles.plaidSection}>
        {loading ? (
          <div>Loading...</div>
        ) : (
          <div className={styles.buttonGroup}>
            <Button
              onClick={() => open()}
              disabled={!ready || loading}
              className={styles.connectButton}
            >
              Connect Bank Account
            </Button>
            <Button
              onClick={handleRefresh}
              disabled={loading}
              className={styles.refreshButton}
            >
              Refresh Transactions
            </Button>
          </div>
        )}
      </div>

      {/* Budget summary boxes below the connect button */}
      {monthlyBudget > 0 && (
        <div className={styles.budgetSections}>
          <BudgetSection
            title="Monthly Needs (50%)"
            amount={monthlyBudget * 0.5}
            description="Essential expenses like housing, food, and utilities"
          />
          <BudgetSection
            title="Monthly Wants (30%)"
            amount={monthlyBudget * 0.3}
            description="Non-essential expenses like entertainment and dining out"
          />
          <BudgetSection
            title="Monthly Savings (20%)"
            amount={monthlyBudget * 0.2}
            description="Savings, investments, and debt repayment"
          />
        </div>
      )}

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.transactionsList}>
        {transactions.map((transaction) => (
          <div key={transaction.id} className={styles.transaction}>
            <div className={styles.transactionInfo}>
              <h3>{transaction.name}</h3>
              <p className={styles.date}>{formatDate(transaction.date)}</p>
              <p className={styles.category}>
                {transaction.category ? transaction.category.join(' > ') : 'Uncategorized'}
              </p>
            </div>
            <div className={styles.transactionAmount}>
              <span className={transaction.amount < 0 ? styles.negative : styles.positive}>
                {formatCurrency(Math.abs(transaction.amount))}
              </span>
              {transaction.pending && <span className={styles.pending}>Pending</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TransactionsPage; 